/**
 * Security Policy Page — Public
 * Minimal but legally sound placeholder.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Server, Eye, Key, AlertTriangle, FileCheck } from 'lucide-react';
import PublicLanguageSwitcher from '../components/PublicLanguageSwitcher';

const SecurityPolicyPage = () => (
  <div className="min-h-screen bg-[#111318] text-[#CBD5E1]">
    <nav className="border-b border-[rgba(255,255,255,0.07)] bg-[#181B23]">
      <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-[#8B95A5] hover:text-white transition-colors"><ArrowLeft size={18} /></Link>
          <Link to="/" className="font-['Syne'] text-white font-semibold text-base">CHARSTAN</Link>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm">Privacy</Link>
          <Link to="/terms" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm">Terms</Link>
          <PublicLanguageSwitcher />
        </div>
      </div>
    </nav>

    <main className="max-w-4xl mx-auto px-6 py-16" data-testid="security-policy-page">
      <div className="flex items-center gap-3 mb-2">
        <Shield size={24} className="text-[#38BDF8]" />
        <h1 className="font-['Syne'] text-3xl font-bold text-white">Security Policy</h1>
      </div>
      <p className="text-[#8B95A5] text-sm mb-10">Last updated: April 2026</p>

      <div className="space-y-8 text-sm leading-relaxed">
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Lock size={16} className="text-[#4ADE80]" />
            <h2 className="text-white font-semibold text-base">1. Data Encryption</h2>
          </div>
          <p>All data transmitted between your browser and the CHARSTAN platform is encrypted using TLS 1.2 or higher. Data at rest is encrypted using AES-256 encryption. Database connections use TLS-encrypted channels. Session cookies are marked Secure, HttpOnly, and SameSite.</p>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-2">
            <Key size={16} className="text-[#38BDF8]" />
            <h2 className="text-white font-semibold text-base">2. Authentication and Access Control</h2>
          </div>
          <p>The platform enforces role-based access control (RBAC) with least-privilege principles. Authentication is managed through secure session tokens with automatic expiration. Multi-provider SSO is supported (Google OAuth, with enterprise SAML/OIDC planned). Rate limiting protects authentication endpoints against brute-force attacks. Account lockout is enforced after repeated failed login attempts.</p>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-2">
            <Eye size={16} className="text-[#E8A838]" />
            <h2 className="text-white font-semibold text-base">3. Audit Trail and Logging</h2>
          </div>
          <p>All significant actions — including logins, data access, exports, document uploads, and configuration changes — are logged in an immutable audit trail. Each entry includes actor identification, timestamp (UTC), action type, and trace ID. Audit logs cannot be modified or deleted by any user, including administrators.</p>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-2">
            <Server size={16} className="text-[#A78BFA]" />
            <h2 className="text-white font-semibold text-base">4. Infrastructure Security</h2>
          </div>
          <p>The platform is hosted on managed cloud infrastructure with network isolation, automated patching, and continuous monitoring. Security headers (Strict-Transport-Security, Content-Security-Policy, X-Frame-Options, X-Content-Type-Options) are applied to all responses. The platform undergoes periodic security scanning using industry-standard tools.</p>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-2">
            <FileCheck size={16} className="text-[#F472B6]" />
            <h2 className="text-white font-semibold text-base">5. Compliance Alignment</h2>
          </div>
          <p>CHARSTAN is designed to support organisations operating under ISO 13485, FDA 21 CFR Part 11, EU GMP Annex 11, ISO 27001, LGPD, and GDPR requirements. The platform provides tools for compliance assessment, gap analysis, and audit readiness — but does not itself constitute certification. An ISO 9001 self-assessment has been conducted and is available upon request.</p>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-[#EF4444]" />
            <h2 className="text-white font-semibold text-base">6. Incident Response</h2>
          </div>
          <p>In the event of a security incident, CHARSTAN will: (a) investigate and contain the incident within 24 hours; (b) notify affected clients within 72 hours of confirmed data breach; (c) provide a written incident report within 14 business days; (d) implement corrective actions and update this policy as needed. All incidents are logged and tracked through the platform's internal CAPA process.</p>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-2">
            <Shield size={16} className="text-[#4ADE80]" />
            <h2 className="text-white font-semibold text-base">7. Vulnerability Disclosure</h2>
          </div>
          <p>If you discover a security vulnerability in the CHARSTAN platform, please report it to <a href="mailto:security@charstan.com" className="text-[#38BDF8] hover:underline">security@charstan.com</a>. We take all reports seriously and will acknowledge receipt within 48 hours. We request that you do not publicly disclose the vulnerability until we have had reasonable time to address it.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">8. Contact</h2>
          <p>For security inquiries, contact <a href="mailto:security@charstan.com" className="text-[#38BDF8] hover:underline">security@charstan.com</a>. For general inquiries, contact <a href="mailto:info@charstan.com" className="text-[#38BDF8] hover:underline">info@charstan.com</a>.</p>
        </section>
      </div>

      <div className="mt-12 pt-6 border-t border-[rgba(255,255,255,0.07)] text-center text-[#8B95A5] text-xs">
        &copy; {new Date().getFullYear()} CHARSTAN. All rights reserved.
      </div>
    </main>
  </div>
);

export default SecurityPolicyPage;
